import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

/**
 * The VAT category catalogue a tenant assigns from (#1353).
 *
 * Read-only: kolula owns the categories and tenants only assign (ADR-0003
 * tier 2). There is no create/update endpoint to expose, deliberately — a
 * tenant typing its own percentage is what a category reference prevents,
 * because rates change and the applicable one is fixed by the date of supply.
 */

export interface TaxCategoryOption {
    id: string;
    /** STANDARD | REDUCED | ZERO | EXEMPT | SMALL_BUSINESS */
    code: string;
    /** EN 16931 BT-118: S | E | Z | AE — a reduced rate is S with its own percent (#1799). */
    vatCategoryCode: string;
    description: string;
    requiresExemptionNote: boolean;
    /**
     * The rate in force for the tenant's country today, as a PERCENT.
     *
     * `null` means no `tax_rate` row covers this country — the category is
     * unusable here and must not be offered, because assigning it would make
     * every booking on that offering unpriceable for VAT. The backend resolves
     * this so no client re-implements the rule it owns.
     */
    currentRatePercent: number | null;
}

export interface TaxCategoriesResponse {
    /** ISO-3166-1 alpha-2, or null when it could not be determined. */
    country: string | null;
    /**
     * The category a NEW offering or add-on starts with (#2959): the tenant's
     * default, prefilled where the operator sees and can change it. Never a
     * pricing fallback. Null when there is none, or when it cannot price in
     * this country — already filtered server-side.
     */
    defaultTaxCategoryId: string | null;
    categories: TaxCategoryOption[];
}

export const taxCategoryApiSlice = createApi({
    reducerPath: 'taxCategoryApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    endpoints: (builder) => ({
        getTaxCategories: builder.query<TaxCategoriesResponse, void>({
            query: () => ({ url: '/tax-category', method: 'GET' }),
        }),
    }),
});

export const { useGetTaxCategoriesQuery } = taxCategoryApiSlice;
